import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LayoutDashboard, ShoppingBag, Package, Users, LogOut, Menu, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
interface NavItemProps {
  icon: React.ReactNode;
  label: string;
  to: string;
  isActive?: boolean;
  onClick?: () => void;
}
function NavItem({
  icon,
  label,
  to,
  isActive,
  onClick
}: NavItemProps) {
  return <Link to={to} onClick={onClick} data-id="element-336">
      <motion.div whileHover={{
      x: 4
    }} whileTap={{
      scale: 0.98
    }} className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-colors relative ${isActive ? 'bg-rose-100 text-rose-600 font-medium' : 'text-gray-500 hover:bg-rose-50 hover:text-rose-500'}`} data-id="element-337">
        <span className={isActive ? 'text-rose-600' : 'text-gray-400 group-hover:text-rose-500'} data-id="element-338">
          {icon}
        </span>
        <span data-id="element-339">{label}</span>
        {isActive && <motion.div layoutId="activeIndicator" className="absolute left-0 w-1 h-8 bg-rose-500 rounded-r-full" data-id="element-340" />}
      </motion.div>
    </Link>;
}
export function DashboardNav() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const {
    user,
    logout
  } = useAuth();
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  const navItems = [{
    icon: <LayoutDashboard size={20} data-id="element-341" />,
    label: 'Dashboard',
    to: '/admin/dashboard'
  }, {
    icon: <ShoppingBag size={20} data-id="element-342" />,
    label: 'Orders',
    to: '/admin/orders'
  }, {
    icon: <Package size={20} data-id="element-343" />,
    label: 'Products',
    to: '/admin/products'
  }, {
    icon: <Users size={20} data-id="element-344" />,
    label: 'Users',
    to: '/admin/users'
  }];
  return <>
      {/* Mobile Menu Button */}
      <div className="lg:hidden fixed top-4 left-4 z-50" data-id="element-345">
        <button onClick={() => setIsOpen(!isOpen)} className="p-2 bg-white rounded-lg shadow-md text-gray-600" data-id="element-346">
          {isOpen ? <X size={24} data-id="element-347" /> : <Menu size={24} data-id="element-348" />}
        </button>
      </div>

      {/* Sidebar */}
      <motion.aside initial={false} animate={{
      x: isOpen ? 0 : -280,
      opacity: isOpen ? 1 : 0
    }} className={`fixed inset-y-0 left-0 z-40 w-72 bg-white border-r border-rose-100 shadow-xl lg:shadow-none lg:translate-x-0 lg:opacity-100 lg:static transition-all duration-300 ${isOpen ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0 lg:translate-x-0 lg:opacity-100'}`} data-id="element-349">
        <div className="h-full flex flex-col p-6" data-id="element-350">
          {/* Logo */}
          <div className="flex items-center space-x-2 mb-10 px-2" data-id="element-351">
            <div className="w-8 h-8 bg-rose-500 rounded-lg flex items-center justify-center text-white font-bold font-serif" data-id="element-352">
              B
            </div>
            <span className="font-serif text-xl font-bold text-gray-900" data-id="element-353">
              Bloom & Pop
            </span>
          </div>

          {/* Nav Items */}
          <nav className="flex-1 space-y-2" data-id="element-354">
            {navItems.map(item => <NavItem key={item.label} icon={item.icon} label={item.label} to={item.to} isActive={location.pathname === item.to} onClick={() => setIsOpen(false)} data-id="element-355" />)}
          </nav>

          {/* User Profile / Logout */}
          <div className="pt-6 border-t border-rose-100" data-id="element-356">
            <button onClick={handleLogout} className="w-full flex items-center space-x-3 px-4 py-3 text-gray-500 hover:text-rose-500 hover:bg-rose-50 rounded-xl transition-colors" data-id="element-357">
              <LogOut size={20} data-id="element-358" />
              <span data-id="element-359">Log Out</span>
            </button>

            <div className="mt-6 flex items-center space-x-3 px-4" data-id="element-360">
              <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center text-rose-500 font-bold" data-id="element-361">
                {user?.name.charAt(0)}
              </div>
              <div className="overflow-hidden" data-id="element-362">
                <p className="text-sm font-medium text-gray-900 truncate" data-id="element-363">
                  {user?.name} {user?.surname}
                </p>
                <p className="text-xs text-gray-500 truncate" data-id="element-364">{user?.email}</p>
              </div>
            </div>
          </div>
        </div>
      </motion.aside>

      {/* Overlay for mobile */}
      {isOpen && <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-30 lg:hidden" onClick={() => setIsOpen(false)} data-id="element-365" />}
    </>;
}